const classCache = new WeakMap<object, Map<string | symbol, any>>();

/**
 * Clears all memoized values for a class instance.
 *
 * @param instance The class instance to clear the cache for.
 */
export function clearMemoized(instance: object) {
  if (classCache.has(instance)) {
    classCache.get(instance)!.clear();
  }
}

/**
 * Memoizes the result of a getter on a class instance.
 * The result is computed once per instance and then cached.
 *
 * @example
 * class MyClass {
 *   _value = 0;
 *
 *   @memoize
 *   get expensiveValue() {
 *     // This is computed only once per instance
 *     return this._value++;
 *   }
 * }
 *
 * const instance = new MyClass();
 * console.log(instance.expensiveValue); // 0
 * console.log(instance.expensiveValue); // 0
 * clearMemoized(instance);
 * console.log(instance.expensiveValue); // 1
 */
export function memoize(_target: any, propertyKey: string | symbol, descriptor: PropertyDescriptor) {
  const originalGet = descriptor.get;

  if (!originalGet) {
    throw new Error("`@memoize` can only be used on getters.");
  }

  descriptor.get = function (this: object) {
    if (!classCache.has(this)) {
      classCache.set(this, new Map<string | symbol, any>());
    }
    const instanceCache = classCache.get(this)!;

    if (instanceCache.has(propertyKey)) {
      return instanceCache.get(propertyKey);
    }

    const value = originalGet.apply(this);
    instanceCache.set(propertyKey, value);
    return value;
  };

  return descriptor;
}
